import React from "react";
import { Modal, Button } from "react-bootstrap";
import { Tooltip } from "@material-ui/core";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import { HiSparkles } from "react-icons/hi";
import styled from "styled-components";

export type AIPrompt = {
  buttonText: string;
  tooltip: string;
  modalTitle: string;
  steps: Array<string>;
  // Se llama al abrir el modal para que el texto refleje el estado actual del paso
  getText: () => string;
};

type Props = {
  // Sin título no se muestra encabezado ni subtítulo (útil para un segundo bloque del mismo paso)
  title?: string;
  subtitle?: string;
  prompts: Array<AIPrompt>;
  // Número del primer prompt, para continuar la numeración entre bloques
  firstNumber?: number;
};

const DEFAULT_SUBTITLE =
  "Usa estos prompts en ChatGPT, Claude u otra IA para documentar este paso más rápido y con mejores prácticas.";

const tooltipTheme = createMuiTheme({
  overrides: {
    MuiTooltip: {
      tooltip: {
        fontSize: "15px",
      },
    },
  },
});

const copyToClipboard = (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied ? Promise.resolve() : Promise.reject(new Error("copy failed"));
};

const StyledBlock = styled.div`
  margin-bottom: 30px;
  padding: 15px 25px 20px;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 5px;
  background: #e8f0f7;
  text-align: center;
`;

const StyledTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  svg {
    flex-shrink: 0;
    color: #f39309;
  }
`;

const StyledBetaBadge = styled.span`
  padding: 1px 8px;
  border-radius: 999px;
  background: #fde7c6;
  color: #6b3a00;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
`;

const StyledSubtitle = styled.div`
  margin: 5px 0 15px;
  font-size: 16px;
`;

const StyledButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const StyledPromptOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledPromptLabel = styled.small`
  margin-bottom: 4px;
`;

const StyledInstructions = styled.div`
  margin-bottom: 15px;
  padding: 15px 25px;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 5px;
  background: #e8f0f7;
  user-select: none;

  ol {
    margin: 5px 0 0;
    padding-left: 20px;
    font-size: 16px;
  }
`;

const StyledPromptText = styled.pre`
  max-height: 320px;
  margin-bottom: 0;
  padding: 15px 25px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 5px;
  background: aliceblue;
  color: inherit;
  font-family: inherit;
  font-size: 16px;
  white-space: pre-wrap;
  user-select: text;
`;

type PromptModalProps = {
  title: string;
  steps: Array<string>;
  promptText: string;
  isOpen: boolean;
  close: () => void;
};

const PromptModal = ({
  title,
  steps,
  promptText,
  isOpen,
  close,
}: PromptModalProps) => {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) setCopied(false);
  }, [isOpen]);

  React.useEffect(() => {
    if (!copied) return undefined;
    const timeout = setTimeout(() => setCopied(false), 4000);
    return () => clearTimeout(timeout);
  }, [copied]);

  const onCopy = () => {
    copyToClipboard(promptText)
      .then(() => setCopied(true))
      .catch((e: Error) => {
        console.log(e.message);
      });
  };

  return (
    <Modal show={isOpen} onHide={close} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <StyledInstructions>
          <div>¿Cómo usar este prompt?</div>
          <ol>
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </StyledInstructions>
        <StyledPromptText>{promptText}</StyledPromptText>
      </Modal.Body>
      <Modal.Footer>
        {copied ? (
          <span className="text-success">
            ¡Copiado! Pégalo ahora en tu IA favorita.
          </span>
        ) : (
          <Button variant="info" onClick={onCopy}>
            Copiar texto completo
          </Button>
        )}
        <Button variant="secondary" onClick={close}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

type OpenPrompt = {
  prompt: AIPrompt;
  text: string;
};

const AIToolsBlock = ({
  title,
  subtitle = DEFAULT_SUBTITLE,
  prompts,
  firstNumber = 1,
}: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  // Se conserva al cerrar para que el contenido no desaparezca durante la animación
  const [openPrompt, setOpenPrompt] = React.useState<OpenPrompt | null>(null);

  const open = (prompt: AIPrompt) => {
    setOpenPrompt({ prompt, text: prompt.getText() });
    setIsOpen(true);
  };

  return (
    <StyledBlock>
      {title && (
        <>
          <StyledTitle>
            <HiSparkles size={20} aria-hidden="true" />
            {title}
            <StyledBetaBadge>Beta</StyledBetaBadge>
          </StyledTitle>
          <StyledSubtitle className="text-muted">{subtitle}</StyledSubtitle>
        </>
      )}
      <MuiThemeProvider theme={tooltipTheme}>
        <StyledButtonRow>
          {prompts.map((prompt, idx) => (
            <StyledPromptOption key={prompt.buttonText}>
              <StyledPromptLabel className="text-muted">
                Prompt {firstNumber + idx}
              </StyledPromptLabel>
              <Tooltip title={prompt.tooltip} placement="bottom" arrow>
                <Button variant="info" onClick={() => open(prompt)}>
                  {prompt.buttonText}
                </Button>
              </Tooltip>
            </StyledPromptOption>
          ))}
        </StyledButtonRow>
      </MuiThemeProvider>
      {openPrompt && (
        <PromptModal
          title={openPrompt.prompt.modalTitle}
          steps={openPrompt.prompt.steps}
          promptText={openPrompt.text}
          isOpen={isOpen}
          close={() => setIsOpen(false)}
        />
      )}
    </StyledBlock>
  );
};

export default AIToolsBlock;
